import {
  getApiUsageBreakdownTimeseries,
  getApiUsageBreakdownGroupby,
} from '@src/api/usageDashboard';
import { trackQueryError } from '@utils/sensor/apptracker';
import { useAtomValue } from 'jotai';
import { get } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { apiAccountsDetailsAtom, intervalAtom, granularityAtom } from '../atoms';
import { transformDataForChart } from '../hooks/utils';
import { breakdownFiltersAtom, breakdownModalVisibleAtom, usageTypeFilterAtom } from './atoms';
import {
  API_BREAKDOWN_TIMESERIES_KEY,
  API_BREAKDOWN_GROUPBY_KEY,
  USAGE_TYPE_MCP_ONLY,
  USAGE_TYPE_API_ONLY,
} from './constants';

import { useApiUsageScope } from '../apiUsageScope';

const METRICS = ['RealtimeAPICalls', 'HistoricalAPICalls'];
const defaultTimeseriesValue = { totals: {}, data: {} };
const defaultGroupbyValue = { totals: {}, data: [] };

const MCP_USER_AGENT = 'conviva-mcp/1.0.0';

const mcpUserAgentRule = (op: 'eq' | 'not_eq') => ({
  field: 'USER_AGENT',
  key: null,
  op,
  value: MCP_USER_AGENT,
  display: `USER_AGENT ${op} ${MCP_USER_AGENT}`,
  originalField: 'USER_AGENT',
});

function useBreakdownRequestBase() {
  const { filter: apiUsageFilter } = useApiUsageScope();
  const apiAccountsDetails = useAtomValue(apiAccountsDetailsAtom);
  const changedInterval: any = useAtomValue(intervalAtom);
  const granularity = useAtomValue(granularityAtom);
  const breakdownFilters = useAtomValue(breakdownFiltersAtom);
  const isModalVisible = useAtomValue(breakdownModalVisibleAtom);
  const usageTypeFilter = useAtomValue(usageTypeFilterAtom);

  const interval = `${changedInterval[0].format()}/${changedInterval[1].format()}`;
  const selectedAccounts = apiAccountsDetails
    .filter((cust) => cust.isSelected === true)
    .map((c) => c.customerId);

  // Build filter in existing IF format: [[rule1], [rule2]] (AND of OR groups)
  const filter = [...apiUsageFilter, ...breakdownFilters.map((f) => [f])];

  // Add MCP/API-Only filter based on usage type dropdown
  if (usageTypeFilter === USAGE_TYPE_MCP_ONLY) {
    filter.push([mcpUserAgentRule('eq')]);
  } else if (usageTypeFilter === USAGE_TYPE_API_ONLY) {
    filter.push([mcpUserAgentRule('not_eq')]);
  }

  return {
    interval,
    granularity,
    accountIds: selectedAccounts,
    filter,
    isEnabled: isModalVisible && selectedAccounts.length > 0,
  };
}

export function useFetchApiUsageBreakdownTimeseries() {
  const { interval, granularity, accountIds, filter, isEnabled } = useBreakdownRequestBase();

  const reqBody = {
    queries: [
      {
        accountIds,
        interval,
        granularity,
        filter,
        options: { withTotals: true },
      },
    ],
  };

  const {
    isFetching,
    isError,
    data: response,
    refetch,
  } = useQuery(
    [API_BREAKDOWN_TIMESERIES_KEY, reqBody],
    () => {
      return getApiUsageBreakdownTimeseries(reqBody).then((res) => {
        const { data, totals } = get(res, ['data', '0'], { data: [], totals: {} });
        return transformDataForChart({ metrics: METRICS, data, totals });
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: isEnabled,
      staleTime: 1 * 60 * 1000,
      placeholderData: defaultTimeseriesValue,
      onError: (err) => {
        trackQueryError({ type: API_BREAKDOWN_TIMESERIES_KEY, err });
      },
    },
  );

  return { isFetching, isError, data: response, refetch };
}

export function useFetchApiUsageBreakdownGroupby(dimension: string) {
  const { interval, accountIds, filter, isEnabled } = useBreakdownRequestBase();

  const reqBody = {
    queries: [
      {
        accountIds,
        interval,
        groupBy: [dimension],
        filter,
        sortBy: [{ metric: 'HistoricalAPICalls', order: 'desc' }],
        limit: 200,
        options: { withTotals: true },
      },
    ],
  };

  const {
    isFetching,
    isError,
    data: response,
    refetch,
  } = useQuery(
    [API_BREAKDOWN_GROUPBY_KEY, dimension, reqBody],
    () => {
      return getApiUsageBreakdownGroupby(reqBody).then((res) => {
        const { data, totals } = get(res, ['data', '0'], { data: [], totals: {} });
        return { data, totals };
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: isEnabled,
      staleTime: 1 * 60 * 1000,
      placeholderData: defaultGroupbyValue,
      onError: (err) => {
        trackQueryError({ type: `${API_BREAKDOWN_GROUPBY_KEY}-${dimension}`, err });
      },
    },
  );

  return { isFetching, isError, data: response, refetch };
}
